import { useMemo } from "react";
import { CountdownTimer } from "./CountdownTimer";
import { WaitlistForm } from "./WaitlistForm";
import EditableText from "./EditableText";

const seededRandom = (seed) => {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const generateStars = (count, seedBase) =>
  Array.from({ length: count }).map((_, i) => {
    const s = seedBase + i;
    return {
      x: seededRandom(s * 1.1) * 100,
      y: seededRandom(s * 2.3) * 100,
      size: 0.5 + seededRandom(s * 3.7) * 2.0,
      opacity: 0.35 + seededRandom(s * 4.1) * 0.6,
      delay: seededRandom(s * 5.9) * 6,
    };
  });

export const Hero = () => {
  const stars = useMemo(() => generateStars(140, 7), []);
  const bigStars = useMemo(() => generateStars(8, 99), []);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden cosmic-bg">
      {/* Star field */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        {stars.map((s, i) => (
          <span
            key={`s-${i}`}
            className="absolute rounded-full bg-[#F4ECFF] twinkle"
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              opacity: s.opacity,
              animationDelay: `${s.delay}s`,
            }}
          />
        ))}
        {bigStars.map((s, i) => (
          <span
            key={`b-${i}`}
            className="absolute rounded-full bg-[#F4ECFF] star-glow twinkle"
            style={{
              left: `${s.x}%`,
              top: `${(s.y * 0.6).toFixed(2)}%`,
              width: `${(s.size + 1.5).toFixed(2)}px`,
              height: `${(s.size + 1.5).toFixed(2)}px`,
              animationDelay: `${s.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Crescent moon */}
      <div aria-hidden className="absolute -top-24 -right-24 sm:top-10 sm:right-16 lg:top-16 lg:right-32 pointer-events-none">
        <div className="relative w-[280px] h-[280px] sm:w-[360px] sm:h-[360px] lg:w-[460px] lg:h-[460px]">
          <div
            className="absolute inset-0 rounded-full blur-3xl opacity-50"
            style={{ background: "radial-gradient(circle, #B87CFF 0%, transparent 60%)" }}
          />
          <svg viewBox="0 0 200 200" className="relative w-full h-full">
            <defs>
              <radialGradient id="moonGradient" cx="40%" cy="40%" r="60%">
                <stop offset="0%" stopColor="#F4ECFF" stopOpacity="1" />
                <stop offset="60%" stopColor="#B87CFF" stopOpacity="0.95" />
                <stop offset="100%" stopColor="#3E1A7A" stopOpacity="0.85" />
              </radialGradient>
              <filter id="moonGlow">
                <feGaussianBlur stdDeviation="2.5" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path d="M120 30 a70 70 0 1 0 0 140 a55 55 0 1 1 0 -140 z" fill="url(#moonGradient)" filter="url(#moonGlow)" />
          </svg>
        </div>
      </div>

      {/* Shooting star */}
      <div aria-hidden className="absolute top-[18%] right-[12%] sm:top-[22%] sm:right-[28%] pointer-events-none">
        <svg width="320" height="120" viewBox="0 0 320 120" className="opacity-80">
          <defs>
            <linearGradient id="trail" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#B87CFF" stopOpacity="0" />
              <stop offset="60%" stopColor="#B87CFF" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#F4ECFF" stopOpacity="1" />
            </linearGradient>
          </defs>
          <path d="M10 100 Q 160 30 300 20" stroke="url(#trail)" strokeWidth="2" fill="none" />
          <circle cx="300" cy="20" r="3" fill="#F4ECFF" className="star-glow" />
        </svg>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#0B0A4A]/60 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 pt-36 sm:pt-44 pb-24 flex flex-col items-center text-center">
        <h2 className="sr-only">
          AI-powered women's wellness companion — cycle syncing, hormone-aware nutrition, fitness, recovery and symptom tracking, designed for the female body.
        </h2>
        <div className="animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <EditableText
            k="hero.eyebrow"
            fallback="Launching August 1, 2026 · Founding access"
            className="eyebrow-light"
            data-testid="hero-eyebrow"
          />
        </div>

        <div className="mt-5 animate-fade-up" style={{ animationDelay: "0.18s" }}>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#B87CFF]/15 border border-[#B87CFF]/40 text-[#F4ECFF] backdrop-blur-md px-4 py-1.5 text-[11px] tracking-[0.18em] uppercase">
            <span className="text-[#B87CFF]">✦</span>
            <EditableText k="hero.by_women" fallback="Built by a woman · For women" />
            <span className="text-[#B87CFF]">✦</span>
          </span>
        </div>

        <h1
          className="editorial-display mt-6 text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl xl:text-8xl text-[#F4ECFF] max-w-5xl animate-fade-up"
          style={{ animationDelay: "0.25s" }}
          data-testid="hero-headline"
        >
          <EditableText
            k="hero.headline"
            fallback={`Your body was never meant to be managed like a man's.`}
            as="span"
            multiline
            className="inline-block"
          />
        </h1>

        <p
          className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-[#F4ECFF]/80 font-sans animate-fade-up"
          style={{ animationDelay: "0.45s" }}
          data-testid="hero-subheadline"
        >
          <EditableText
            k="hero.subheadline"
            fallback="Feminine Ways is the AI-powered women's wellness companion that syncs nutrition, fitness, recovery, hormones and your cycle — adapting to you in real time. Join the waitlist and lock in $13/month for life (or just $10/month annual) instead of $25."
            multiline
          />
        </p>

        <div id="waitlist" className="mt-10 w-full max-w-xl animate-fade-up" style={{ animationDelay: "0.6s" }}>
          <WaitlistForm variant="dark" testIdPrefix="hero" source="hero" cta="Claim $13/month" />
          <EditableText
            k="hero.microcopy"
            fallback="No spam. Just launch day & your founder rate."
            as="p"
            className="mt-3 text-xs text-[#F4ECFF]/60 tracking-wide"
            data-testid="hero-microcopy"
          />
        </div>

        <div className="mt-14 sm:mt-16 animate-fade-up" style={{ animationDelay: "0.8s" }}>
          <EditableText
            k="hero.countdown_label"
            fallback="Launching in"
            as="p"
            className="eyebrow-light mb-4"
            data-testid="countdown-label"
          />
          <CountdownTimer />
        </div>

        <div className="mt-20 sm:mt-24 flex flex-col items-center gap-2 text-[#B87CFF]/80">
          <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#B87CFF]/60 to-transparent" />
          <EditableText k="hero.scroll" fallback="Explore the app" as="span" className="text-[10px] uppercase tracking-[0.3em]" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
